import { siteMetadata } from '../types/meta';
import type { ProductDetail } from '../types/types';
import { toAbsoluteUrl } from './seo';
import { getProductIdFromShopifyId } from './shopify-helper';

// JSON-LD structured data (https://schema.org) for search engines, e.g. price, stock and breadcrumbs in Google results.
// Used as a route head() script, rendered by <HeadContent /> in the document head. Test: https://search.google.com/test/rich-results

/**
 * A <script type="application/ld+json"> for a route's head().scripts.
 * NOTE: head scripts are inserted as raw HTML, so "<" is escaped (e.g. a "</script>" in a product description).
 */
function jsonLd(data: Record<string, unknown>) {
  return {
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      ...data
    }).replace(/</g, '\\u003c') // JSON unicode escape, read back as "<" by JSON-LD parsers
  };
}

const productsCollection = { name: 'Karkötők', path: '/karkotok' };

/** The shop (brand, logo, contacts, social profiles), for the home page. */
export function organizationJsonLd() {
  return jsonLd({
    '@type': 'Organization',
    name: siteMetadata.title,
    url: siteMetadata.siteUrl,
    logo: toAbsoluteUrl(siteMetadata.image),
    email: siteMetadata.email,
    sameAs: siteMetadata.socialProfiles
  });
}

/** The site name shown in search results, for the home page. */
export function webSiteJsonLd() {
  return jsonLd({
    '@type': 'WebSite',
    name: siteMetadata.title,
    url: `${siteMetadata.siteUrl}/`,
    inLanguage: 'hu'
  });
}

/**
 * A product with an offer per variant (size).
 * NOTE: availability is the state at build time (the site is statically generated), so it is as fresh as the last build.
 */
export function productJsonLd(product: NonNullable<ProductDetail>) {
  const url = toAbsoluteUrl(`/product/${product.handle}`);

  return jsonLd({
    '@type': 'Product',
    name: product.title,
    description: product.description,
    url,
    image: product.images.nodes.map(image => image.url),
    brand: { '@type': 'Brand', name: siteMetadata.title },
    offers: product.variants.nodes.map(variant => ({
      '@type': 'Offer',
      name: variant.title,
      sku: getProductIdFromShopifyId(variant.id),
      url,
      price: variant.price.amount,
      priceCurrency: variant.price.currencyCode,
      availability:
        variant.availableForSale ?
          'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition'
    }))
  });
}

/** Home > Karkötők > product, shown instead of the URL in search results. */
export function productBreadcrumbJsonLd(product: NonNullable<ProductDetail>) {
  const items = [
    { name: 'Kezdőlap', path: '/' },
    productsCollection,
    { name: product.title, path: `/product/${product.handle}` }
  ];

  return jsonLd({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path)
    }))
  });
}
